/**
 * adminMarketplaceStore — Marketplace slice of the admin store (Zustand).
 *
 * Endpoints:
 *   GET   /api/admin/marketplace/purchases
 *   POST  /api/admin/marketplace/purchases/:transactionId/revoke
 *   POST  /api/admin/marketplace/purchases/:transactionId/restore
 *
 *   GET   /api/admin/marketplace/:type
 *   GET   /api/admin/marketplace/:type/:id
 *   PATCH /api/admin/marketplace/:type/:id
 *   PATCH /api/admin/marketplace/:type/:id/approve
 *   PATCH /api/admin/marketplace/:type/:id/reject
 *   PATCH /api/admin/marketplace/:type/:id/restore
 *   PATCH /api/admin/marketplace/:type/:id/withdraw
 *
 * `:type` is one of: project-topics, seminar-topics, research-materials, manuals.
 */
import { API_URL } from "../../constants";
import { toDisplayString } from "../../utils/toDisplayString";
import { authHeaders, resolveToken } from "./adminUtils";

export const marketplaceInitialState = {
  // Purchases / revenue
  marketplacePurchases: [],
  marketplacePurchasesLoading: false,
  marketplacePurchasesError: null,
  marketplacePurchasesTotal: 0,
  marketplacePurchasesPages: 1,

  // Moderation queue counts keyed by resource type
  marketplaceModerationCounts: null,

  // Marketplace resources
  marketplaceResources: [],
  marketplaceResourcesType: "project-topics",
  marketplaceResourcesLoading: false,
  marketplaceResourcesError: null,
  marketplaceResourcesTotal: 0,
  marketplaceResourcesPages: 1,

  // Single resource
  marketplaceResourceDetail: null,
  marketplaceResourceDetailLoading: false,
  marketplaceResourceDetailError: null,

  // Mutations
  marketplaceMutationLoading: false,
  marketplaceMutationError: null,
};

/** Normalize the paginated wrapper (`items/total/totalPages`) defensively. */
function normalizeListPayload(data) {
  const payload = data?.data || data || {};
  if (Array.isArray(payload)) {
    return { items: payload, total: payload.length, totalPages: 1 };
  }
  return {
    items: payload.items || payload.purchases || payload.resources || [],
    total: payload.total ?? payload.totalItems ?? 0,
    totalPages: payload.totalPages ?? payload.pages ?? 1,
  };
}

async function apiRequest(get, path, { method = "GET", body } = {}) {
  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers: authHeaders(resolveToken(get)),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const text = await response.text();
  let data = {};
  if (text) {
    const contentType = response.headers.get("content-type") || "";
    const looksJson =
      contentType.includes("application/json") ||
      text.trim().startsWith("{") ||
      text.trim().startsWith("[");
    if (looksJson) {
      try {
        data = JSON.parse(text);
      } catch {
        data = {};
      }
    }
  }

  if (!response.ok || (data && data.error)) {
    throw new Error(
      toDisplayString(data?.error ?? data?.message, `Request failed (${response.status})`)
    );
  }

  return data;
}

function buildQuery(params = {}) {
  return new URLSearchParams(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== "")
  ).toString();
}

export function createMarketplaceActions(set, get) {
  async function mutate(path, method, body, successMessage) {
    set({ marketplaceMutationLoading: true, marketplaceMutationError: null });
    try {
      const data = await apiRequest(get, path, { method, body });
      set({
        marketplaceMutationLoading: false,
        marketplaceMutationError: null,
        successMessage,
      });
      return { success: true, data: data?.data ?? data };
    } catch (err) {
      const message = toDisplayString(err.message, "Action failed");
      set({ marketplaceMutationLoading: false, marketplaceMutationError: message });
      return { success: false, error: message };
    }
  }

  return {
    // ── Purchases ───────────────────────────────────────────────────────────
    fetchMarketplacePurchases: async (params = {}) => {
      set({ marketplacePurchasesLoading: true, marketplacePurchasesError: null });
      try {
        const query = buildQuery(params);
        const data = await apiRequest(
          get,
          `/api/admin/marketplace/purchases${query ? `?${query}` : ""}`
        );
        const { items, total, totalPages } = normalizeListPayload(data);
        set({
          marketplacePurchasesLoading: false,
          marketplacePurchases: items,
          marketplacePurchasesTotal: total,
          marketplacePurchasesPages: totalPages,
        });
        return { success: true, purchases: items, total, pages: totalPages };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to fetch marketplace purchases");
        set({
          marketplacePurchasesLoading: false,
          marketplacePurchasesError: message,
          marketplacePurchases: [],
        });
        return { success: false, error: message };
      }
    },

    revokeMarketplacePurchase: (transactionId, reason = "") =>
      mutate(
        `/api/admin/marketplace/purchases/${transactionId}/revoke`,
        "POST",
        { reason },
        "Purchase access revoked"
      ),

    restoreMarketplacePurchase: (transactionId) =>
      mutate(
        `/api/admin/marketplace/purchases/${transactionId}/restore`,
        "POST",
        undefined,
        "Purchase access restored"
      ),

    // ── Moderation counts (one request per resource type, limit 1) ──────────
    fetchMarketplaceModerationCounts: async () => {
      const types = ["project-topics", "seminar-topics", "research-materials", "manuals"];
      try {
        const entries = await Promise.all(
          types.map(async (type) => {
            const query = buildQuery({ status: "PENDING", page: 1, limit: 1 });
            const data = await apiRequest(
              get,
              `/api/admin/marketplace/${type}?${query}`
            );
            const { total } = normalizeListPayload(data);
            return [type, total];
          })
        );
        const counts = Object.fromEntries(entries);
        counts.total = entries.reduce((sum, [, value]) => sum + value, 0);
        set({ marketplaceModerationCounts: counts });
        return { success: true, counts };
      } catch (err) {
        return { success: false, error: toDisplayString(err.message, "Failed to fetch counts") };
      }
    },

    // ── Marketplace resources ───────────────────────────────────────────────
    fetchMarketplaceResources: async (type, params = {}) => {
      set({
        marketplaceResourcesLoading: true,
        marketplaceResourcesError: null,
        marketplaceResourcesType: type,
      });
      try {
        const query = buildQuery(params);
        const data = await apiRequest(
          get,
          `/api/admin/marketplace/${type}${query ? `?${query}` : ""}`
        );
        const { items, total, totalPages } = normalizeListPayload(data);
        set({
          marketplaceResourcesLoading: false,
          marketplaceResources: items,
          marketplaceResourcesTotal: total,
          marketplaceResourcesPages: totalPages,
        });
        return { success: true, resources: items, total, pages: totalPages };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to fetch marketplace resources");
        set({
          marketplaceResourcesLoading: false,
          marketplaceResourcesError: message,
          marketplaceResources: [],
        });
        return { success: false, error: message };
      }
    },

    fetchMarketplaceResource: async (type, id) => {
      set({ marketplaceResourceDetailLoading: true, marketplaceResourceDetailError: null });
      try {
        const data = await apiRequest(get, `/api/admin/marketplace/${type}/${id}`);
        const detail = data?.data ?? data ?? null;
        set({ marketplaceResourceDetailLoading: false, marketplaceResourceDetail: detail });
        return { success: true, resource: detail };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to fetch marketplace resource");
        set({
          marketplaceResourceDetailLoading: false,
          marketplaceResourceDetailError: message,
          marketplaceResourceDetail: null,
        });
        return { success: false, error: message };
      }
    },

    updateMarketplaceResource: (type, id, payload = {}) =>
      mutate(
        `/api/admin/marketplace/${type}/${id}`,
        "PATCH",
        payload,
        "Marketplace resource updated"
      ),

    approveMarketplaceResource: (type, id) =>
      mutate(
        `/api/admin/marketplace/${type}/${id}/approve`,
        "PATCH",
        undefined,
        "Marketplace resource approved"
      ),

    rejectMarketplaceResource: (type, id, reason = "") =>
      mutate(
        `/api/admin/marketplace/${type}/${id}/reject`,
        "PATCH",
        { reason },
        "Marketplace resource rejected"
      ),

    restoreMarketplaceResource: (type, id) =>
      mutate(
        `/api/admin/marketplace/${type}/${id}/restore`,
        "PATCH",
        undefined,
        "Marketplace resource restored"
      ),

    withdrawMarketplaceResource: (type, id) =>
      mutate(
        `/api/admin/marketplace/${type}/${id}/withdraw`,
        "PATCH",
        undefined,
        "Marketplace resource withdrawn"
      ),
  };
}

const marketplaceActions = createMarketplaceActions;
export default marketplaceActions;
